import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox, Divider, Select, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { getZonesByRegion } from '../../../service/MySrcmService';
import { displayError } from '../../../shared/Utils';
import { useToastMessage } from '../../../hooks/useToastMessage';

const { Option } = Select;

interface OptionData {
	value: string | undefined;
	label: string | undefined;
	regionId?: number;
}

interface HomeZoneOptionData {
	value: string;
}

interface RemoteSingleSelectProps {
	onChange?: (value: OptionData[]) => void;
	value?: OptionData[] | null | undefined;
	authorizedRegionIds?: Array<number>;
	isFunctionaryCareTeam: boolean;
	homeZone?: HomeZoneOptionData;
	authorizedRegion?: OptionData[];
}

const RemoteSingleSelect: React.FC<RemoteSingleSelectProps> = ({
	onChange,
	value,
	authorizedRegionIds,
	isFunctionaryCareTeam,
	authorizedRegion,
}: RemoteSingleSelectProps) => {
	const { t } = useTranslation();
	const { showToastMessage } = useToastMessage();
	const [options, setOptions] = useState<OptionData[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedValue, setSelectedValue] = useState<OptionData[]>(value || []);
	const [filteredOptions, setFilteredOptions] = useState<OptionData[]>([]);
	const [open, setOpen] = useState(false);
	const zonesCacheRef = useRef<Record<number, OptionData[]>>({});

	const allValues = options.map((opt) => opt.value);
	const selectedValuesOnly = selectedValue
		.map((item) => item.value)
		.filter((val): val is string => val !== undefined);
	const isAllSelected = selectedValuesOnly.length === allValues.length;
	const isIndeterminate = selectedValuesOnly.length > 0 && !isAllSelected;

	const fetchData = useCallback(
		async (searchValue: string) => {
			setLoading(true);
			if (authorizedRegionIds?.length) {
				try {
					const missingRegionIds = authorizedRegionIds.filter(
						(id) => !zonesCacheRef.current[id]
					);
					const zoneResponses = await Promise.all(
						missingRegionIds.map((regionId) => getZonesByRegion(regionId))
					);

					zoneResponses.forEach((response, index) => {
						const regionId = missingRegionIds[index];

						zonesCacheRef.current[regionId] = (response.results ?? []).map(
							(z) => ({
								value: String(z.id),
								label: z.name,
								regionId,
							})
						);
					});

					const zoneOptions = authorizedRegionIds.flatMap(
						(regionId) => zonesCacheRef.current[regionId] ?? []
					);

					setOptions(zoneOptions);

					if (!searchValue) {
						setFilteredOptions(zoneOptions);
					} else {
						const lower = searchValue.toLowerCase();
						setFilteredOptions(
							zoneOptions.filter((opt) =>
								opt.label?.toLowerCase().includes(lower)
							)
						);
					}
				} catch (err) {
					const errorMsg = displayError(err);
					showToastMessage('error', errorMsg);
				} finally {
					setLoading(false);
				}
				return;
			}

			setOptions([]);
			setFilteredOptions([]);
			setSelectedValue([]);

			setLoading(false);
		},
		[authorizedRegionIds, showToastMessage]
	);

	useEffect(() => {
		if (options.length > 0) {
			const validZoneIds = new Set(options.map((zone) => String(zone.value)));

			const filteredSelectedZones = selectedValue.filter((zone) =>
				validZoneIds.has(String(zone.value))
			);

			const hasChanged = filteredSelectedZones.length !== selectedValue.length;

			if (hasChanged) {
				setSelectedValue(filteredSelectedZones);
			}
		}
	}, [options, onChange, selectedValue]);

	const regionIdsKey = (authorizedRegionIds ?? []).join(',');

	useEffect(() => {
		void fetchData('');
	}, [regionIdsKey, fetchData]);

	const handleSearch = (searchKey: string) => {
		setOpen(true);
		if (!searchKey) {
			setFilteredOptions(options);
		} else {
			const lower = searchKey.toLowerCase();
			const filtered = options.filter((item) =>
				item.label?.toLowerCase().includes(lower)
			);
			setFilteredOptions(filtered);
		}
	};

	const handleChange = (values: { label: string; value: string }[]) => {
		const selected = values.map((val) => {
			const matchedOption = options.find((opt) => opt.value === val.value);
			return {
				value: val.value,
				label: val.label,
				regionId: matchedOption?.regionId,
			};
		});
		setSelectedValue(selected);
		onChange?.(selected);
		setFilteredOptions(options);
		setOpen(true);
	};

	const handleSelectAll = () => {
		if (isAllSelected) {
			setSelectedValue([]);
			onChange?.([]);
		} else {
			setSelectedValue(options);
			onChange?.(options);
		}
		setFilteredOptions(options);
	};

	useEffect(() => {
		if (value) {
			setSelectedValue(value);
		}
	}, [value]);

	return (
		<Select
			mode="multiple"
			showSearch
			open={open}
			onDropdownVisibleChange={(o) => setOpen(o)}
			filterOption={false}
			labelInValue
			defaultActiveFirstOption={false}
			placeholder={t('searchZones')}
			value={selectedValue as { label: string; value: string }[]}
			onSearch={handleSearch}
			onChange={handleChange}
			notFoundContent={loading ? <Spin size="small" /> : null}
			className="font-sans text-sm text-secondary-gray [&_.ant-select-selector]:!h-[150px] [&_.ant-select-selector]:!overflow-y-auto [&_.ant-select-selector]:!overflow-x-hidden"
			size="large"
			suffixIcon={null}
			optionLabelProp="label"
			disabled={
				!authorizedRegion ||
				authorizedRegion.length === 0 ||
				isFunctionaryCareTeam
			}
			dropdownRender={(menu) => (
				<>
					<div
						className="px-2 py-3 cursor-pointer flex justify-between"
						onClick={handleSelectAll}
					>
						<span>Select All</span>
						<Checkbox
							checked={isAllSelected}
							indeterminate={isIndeterminate}
							style={{ marginRight: 8 }}
						/>
					</div>
					<Divider style={{ margin: 0 }} />
					{menu}
				</>
			)}
		>
			{filteredOptions.map((opt) => (
				<Option key={opt.value} value={opt.value} label={opt.label}>
					<div className="flex items-center">{opt.label}</div>
				</Option>
			))}
		</Select>
	);
};

export default RemoteSingleSelect;
